import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import Hero from '../../components/Hero/Hero';
import ProductCard from '../../components/ProductCard/ProductCard';
import SoftwareShowcase from '../../components/SoftwareShowcase/SoftwareShowcase';
import { featuredProducts } from '../../data/products';
import { emailUtils } from '../../utils/contactUtils';
import OptimizedImage from '../../components/Image/OptimizedImage';

const Home = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  
  // 轮播相关状态
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // 图片放大预览状态
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  
  // 检测是否为RTL语言
  const isRTL = i18n.language === 'ar';
  
  // 调试信息
  useEffect(() => {
    console.log('Current language:', i18n.language);
    console.log('Is RTL:', isRTL);
    console.log('Document direction:', document.documentElement.dir);
    console.log('Company photos count:', companyPhotos.length);
  }, [i18n.language, isRTL]);
  
  // 公司照片数据 - 可以轻松添加更多图片
  const companyPhotos = [
    { src: '/images/company/25e539787ecb6c0d7ce87b29dcdea23.jpg', alt: '研发现场' },
    { src: '/images/company/WX20250718-183637.png', alt: '办公环境' },
    { src: '/images/company/customer-service.jpg', alt: '客户服务' },
    { src: '/images/company/280c3cfcec5571194306b4bae30752.jpg', alt: '团队合作' },
    { src: '/images/company/0127c5413cd18d35215acdf6c3beb8.jpg', alt: '产品展示' },
    { src: '/images/company/5bab735c6e876f7d0af33de2e173db.jpg', alt: '技术交流' },
    { src: '/images/company/929175a0b71b1c41d7d524967ed2e1.jpg', alt: '工作场景' },
    { src: '/images/company/63d1af2d92c768ff4213d8066a923a.jpg', alt: '企业文化' },
    { src: '/images/company/9baa335c68ff447e8baf718f478ff8.jpg', alt: '团队建设' },
    { src: '/images/company/f1dbf5d955e3c92d3e1d40474d9ac2.jpg', alt: '业务洽谈' },
    { src: '/images/company/9f8cae8baeb67cd31ce7a44970c2b6.jpg', alt: '创新研发' }
  ];
  
  // 自动轮播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => prev + 1);
    }, 4000); // 4秒自动切换
    
    return () => clearInterval(timer);
  }, [companyPhotos.length]);
  
  // 无缝循环处理
  useEffect(() => {
    // 当滚动到最后的复制图片时，瞬间跳回到原始位置
    if (currentSlide >= companyPhotos.length) {
      const timer = setTimeout(() => {
        setCurrentSlide(0);
      }, 500); // 等待动画完成后跳转
      return () => clearTimeout(timer);
    }
    // 当滚动到最前面的复制图片时，瞬间跳到末尾位置
    if (currentSlide < 0) {
      const timer = setTimeout(() => {
        setCurrentSlide(companyPhotos.length - 1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, companyPhotos.length]);
  
  // 键盘事件监听 - ESC关闭图片预览，左右键切换图片
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedImageIndex(null);
      } else if (selectedImageIndex !== null) {
        if (event.key === 'ArrowLeft') {
          isRTL ? nextImage() : prevImage();
        } else if (event.key === 'ArrowRight') {
          isRTL ? prevImage() : nextImage();
        }
      }
    };
    
    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedImageIndex, isRTL]);
  
  // 手动切换
  const nextSlide = () => {
    setCurrentSlide((prev) => prev + 1);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => prev - 1);
  };
  
  // 打开图片预览 - 传入图片索引
  const openImagePreview = (index: number) => {
    setSelectedImageIndex(index);
  };
  
  // 关闭图片预览
  const closeImagePreview = () => {
    setSelectedImageIndex(null);
  };
  
  // 预览模态框中的图片切换
  const nextImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex + 1) % companyPhotos.length);
    }
  };
  
  const prevImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex - 1 + companyPhotos.length) % companyPhotos.length);
    }
  };

  const solutionsPreview = [
    {
      key: 'k12Libraries',
      icon: '🏫'
    },
    {
      key: 'publicLibraries', 
      icon: '🏛️'
    },
    {
      key: 'specializedLibraries',
      icon: '🎓'
    }
  ];

  return (
    <div className="min-h-screen">
      <Hero />
      
      {/* Featured Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('pages.products.featured.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('pages.products.featured.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="h-full"
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link 
              to="/products"
              className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {t('navigation.products')}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Software Showcase Section */}
      <SoftwareShowcase />

      {/* Solutions Preview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('solutions.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('solutions.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutionsPreview.map((solution, index) => (
              <motion.div
                key={solution.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => console.log(`${solution.key} clicked`)}
              >
                <div className="text-4xl mb-4">{solution.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {t(`pages.solutions.${solution.key}.title`)}
                </h3>
                <p className="text-gray-600">
                  {t(`pages.solutions.${solution.key}.subtitle`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Photos Carousel Section - 轮播图片展示 */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-blue-700 mb-4">
              {t('common.companyPhotos.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('common.companyPhotos.description')}
            </p>
          </motion.div>
          
          {/* 轮播容器 */}
          <div className={`relative ${isRTL ? 'rtl' : 'ltr'}`}>
            {/* 左右控制按钮 */}
            <button
              onClick={isRTL ? nextSlide : prevSlide}
              className={`absolute ${isRTL ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronLeft className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={isRTL ? prevSlide : nextSlide}
              className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronRight className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            
            {/* 图片展示区域 */}
            <div className="overflow-hidden rounded-xl">
              <motion.div
                className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
                animate={{ 
                  x: isRTL 
                    ? `${(currentSlide + companyPhotos.length) * (100 / 3)}%`
                    : `${-(currentSlide + companyPhotos.length) * (100 / 3)}%`
                }}
                transition={{ 
                  duration: currentSlide < 0 || currentSlide >= companyPhotos.length ? 0 : 0.5, 
                  ease: "easeInOut" 
                }}
              >
                {/* 前面的复制图片 - 用于无缝循环 */}
                {companyPhotos.map((photo, index) => (
                  <motion.div
                    key={`prev-${index}`}
                    className="flex-shrink-0 w-1/3 px-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div
                      className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => openImagePreview(index)}
                    >
                      <OptimizedImage 
                        src={photo.src} 
                        alt={photo.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      {/* 放大图标提示 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* 原始图片 */}
                {companyPhotos.map((photo, index) => (
                  <motion.div
                    key={index}
                    className="flex-shrink-0 w-1/3 px-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div
                      className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => openImagePreview(index)}
                    >
                      <OptimizedImage 
                        src={photo.src} 
                        alt={photo.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      {/* 放大图标提示 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* 后面的复制图片 - 用于无缝循环 */}
                {companyPhotos.map((photo, index) => (
                  <motion.div
                    key={`next-${index}`}
                    className="flex-shrink-0 w-1/3 px-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div
                      className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => openImagePreview(index)}
                    >
                      <OptimizedImage 
                        src={photo.src} 
                        alt={photo.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      {/* 放大图标提示 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
            
            {/* 指示器 */}
            <div className="flex justify-center mt-6 space-x-2">
              {companyPhotos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    (currentSlide % companyPhotos.length) === index 
                      ? 'bg-blue-600 scale-110' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 图片预览模态框 */}
      {selectedImageIndex !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 关闭按钮 */}
            <button
              onClick={closeImagePreview}
              className={`absolute -top-4 ${isRTL ? '-left-4' : '-right-4'} z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors`}
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            
            {/* 放大的图片 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
              <OptimizedImage
                src={companyPhotos[selectedImageIndex].src}
                alt={companyPhotos[selectedImageIndex].alt}
                className="w-full h-full object-contain max-h-[80vh]"
              />
            </div>
            
            {/* 左右切换按钮 */}
            <button
              onClick={isRTL ? nextImage : prevImage}
              className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronLeft className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={isRTL ? prevImage : nextImage}
              className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-90`}
            >
              <ChevronRight className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>

            {/* 当前图片索引指示器 */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {selectedImageIndex + 1} / {companyPhotos.length}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('common.cta.readyToTransform')}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {t('common.cta.contactDescription')}
            </p>
            <div className="flex justify-center">
              <button 
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                onClick={() => emailUtils.openConsultationEmail(t)}
              >
                {t('common.cta.getFreeConsultation')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 